import { PROJECT_ROLES, type ProjectRole } from './types/factory';

export type { ProjectRole };

/**
 * Project permissions by effective role (docs/first-slice-plan.md §2). A user's effective role on a
 * project: maintainer when they're an admin; else the highest of their direct grant and their
 * organisations' grants; none means no access (the project answers 404).
 *
 * Admin-only actions (create/archive projects, organisations, users, invites, machines, project
 * secrets) aren't here: they use `requireAdmin`.
 */
export const Permissions = {
  /** Read the board, tasks, runs, members and settings. */
  PROJECT_VIEW: ['viewer', 'member', 'maintainer'] as ProjectRole[],
  /** Create, edit, comment, move, attach, follow. */
  TASKS_EDIT: ['member', 'maintainer'] as ProjectRole[],
  /** Columns, labels and types. */
  TASKS_MANAGE: ['maintainer'] as ProjectRole[],
  /** Start and cancel runs. */
  RUNS_START: ['maintainer'] as ProjectRole[],
  /** Merge a task's pull request from its card (T-3). */
  PRS_MERGE: ['maintainer'] as ProjectRole[],
  /** Edit the project, manage its grants and its checkouts' links. */
  PROJECT_SETTINGS: ['maintainer'] as ProjectRole[],
  /** Create, rename and delete the project's saved views (saved filters). */
  SAVED_VIEWS_EDIT: ['member', 'maintainer'] as ProjectRole[],
} as const;

export type Permission = keyof typeof Permissions;

export const ProjectRoleLabels: Record<ProjectRole, string> = {
  viewer: 'Viewer',
  member: 'Member',
  maintainer: 'Maintainer',
};

export function isProjectRole(value: unknown): value is ProjectRole {
  return typeof value === 'string' && (PROJECT_ROLES as readonly string[]).includes(value);
}

/** Higher is more: viewer 1, member 2, maintainer 3. */
export function projectRoleRank(role: ProjectRole | null | undefined): number {
  return role ? PROJECT_ROLES.indexOf(role) + 1 : 0;
}

/** The higher of two roles (null when both are null). */
export function maxProjectRole(a: ProjectRole | null | undefined, b: ProjectRole | null | undefined): ProjectRole | null {
  const ra = projectRoleRank(a);
  const rb = projectRoleRank(b);
  if (!ra && !rb) return null;
  return (ra >= rb ? a : b) as ProjectRole;
}

/**
 * A user's effective role on a project: maintainer for admins; else the highest of the grants
 * (their direct grant and each of their organisations' grants); null for no access.
 */
export function effectiveProjectRole(isAdmin: boolean, grantRoles: Array<ProjectRole | string | null | undefined>): ProjectRole | null {
  if (isAdmin) return 'maintainer';
  let best: ProjectRole | null = null;
  for (const role of grantRoles) {
    if (isProjectRole(role)) best = maxProjectRole(best, role);
  }
  return best;
}

export function hasPermission(role: ProjectRole | null | undefined, permission: Permission): boolean {
  return !!role && Permissions[permission].includes(role);
}

/**
 * Tasks API (/api/tasks): view, edit, manage (columns, labels, types), merge (a task's pull request, T-3)
 * and fixConflicts (hand a conflicting PR to the AI builder, SF-T-79: it starts a run). A patch request
 * (T-57) is decided by admins only (requireAdmin).
 */
export const TaskRoutePermissions = {
  view: 'PROJECT_VIEW',
  edit: 'TASKS_EDIT',
  manage: 'TASKS_MANAGE',
  merge: 'PRS_MERGE',
  fixConflicts: 'RUNS_START',
  /** SF-T-177: deny a run's approval request (its agent's turn restarts), explain it again, or answer its agent's own T3 question. */
  answerApproval: 'RUNS_START',
} as const satisfies Record<string, Permission>;

/** Saved filters (/api/saved-filters): everyone on the project reads; SAVED_VIEWS_EDIT saves, renames and deletes. */
export const SavedFilterRoutePermissions = {
  view: 'PROJECT_VIEW',
  edit: 'SAVED_VIEWS_EDIT',
} as const satisfies Record<string, Permission>;

/** The project itself (/api/project, /api/project/members, /api/project/grants). */
export const ProjectRoutePermissions = {
  view: 'PROJECT_VIEW',
  settings: 'PROJECT_SETTINGS',
} as const satisfies Record<string, Permission>;

/** SF-T-56: the project's own agent files on the Agent config page (/api/project/agent-config): maintainers (and admins). */
export const ProjectAgentConfigRoutePermissions = {
  view: 'PROJECT_SETTINGS',
} as const satisfies Record<string, Permission>;

/**
 * Runs (/api/runs): everyone on the project reads; maintainers start, cancel and wrap up (SF-T-209). Members
 * talk to a run's agent from the task's Agent tab (`message`: Steer or Queue).
 */
export const RunRoutePermissions = {
  view: 'PROJECT_VIEW',
  start: 'RUNS_START',
  message: 'TASKS_EDIT',
} as const satisfies Record<string, Permission>;

/**
 * SF-T-209: a task's worktree and dev servers (/api/dev-servers): everyone reads; Members start them and set
 * the task's idle time; Maintainers stop them and tidy the worktree.
 */
export const DevServerRoutePermissions = {
  view: 'PROJECT_VIEW',
  start: 'TASKS_EDIT',
  stop: 'RUNS_START',
} as const satisfies Record<string, Permission>;

/** LLM usage (/api/usage, T-12): everyone on the project reads a task's and a run's. */
export const UsageRoutePermissions = {
  view: 'PROJECT_VIEW',
} as const satisfies Record<string, Permission>;

/**
 * SF-T-43: the project's dashboard (/api/project/dashboard): everyone on the project reads it (AI cost is
 * admins' only, left out of anyone else's answer); maintainers change its layout.
 */
export const DashboardRoutePermissions = {
  view: 'PROJECT_VIEW',
  edit: 'PROJECT_SETTINGS',
} as const satisfies Record<string, Permission>;
